import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import SaveToCollection from "./SaveToCollection";

// "How would a native speaker write all this?" The corrections fix one sentence at a
// time; this is everything written so far, rewritten the way a native speaker would -
// how the ideas connect, the idioms, the structure.
//
// Generated only on request - each click of the page's "Native speaker version" button (run goes
// up by one) - so writing more doesn't quietly spend checks. Once the text has changed
// since this version was made, the card says so.
//
// Each sentence can be saved to a collection (Quick Capture, as with the ✓ sentences)
// and opened to show its translation and a short note on what's worth noticing.
export default function NativeVersion({
  baseLanguageName, onClose, quickCapture, request, rewriteUrl, run, targetLanguageIso, text,
}) {
  const [result, setResult] = useState(null);
  const [basedOn, setBasedOn] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openIndexes, setOpenIndexes] = useState(new Set());
  const cardRef = useRef(null);

  // Read through refs: a run uses the text as it is at the click, and nothing but a new
  // run - not a changed explanation language, which changes request - fetches again.
  const latest = useRef({ request, rewriteUrl, text });
  latest.current = { request, rewriteUrl, text };

  const generate = useCallback(async () => {
    const source = latest.current.text.trim();
    if (!source) return;

    setLoading(true);
    setError(null);

    try {
      const json = await latest.current.request(latest.current.rewriteUrl, { text: source });
      setResult(json.sentences);
      setBasedOn(source);
      setOpenIndexes(new Set());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    generate();
    if (cardRef.current && cardRef.current.scrollIntoView) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [generate, run]);

  const toggle = (index) => {
    setOpenIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const stale = !!basedOn && basedOn !== text.trim();

  return (
    <div className="ftf-native" ref={cardRef}>
      <div className="ftf-native-head">
        <span className="ftf-label">How a native speaker might write it</span>
        <button aria-label="Close" className="ftf-idea-close" onClick={onClose} type="button">×</button>
      </div>

      {loading && <div className="ftf-muted">Rewriting…</div>}
      {!loading && stale && (
        <div className="ftf-muted">You&apos;ve changed your text since - click Native speaker version again to update this.</div>
      )}
      {!loading && error && <div className="ftf-error">{error}</div>}
      {!loading && !error && result && (
        <ol className="ftf-native-sentences">
          {result.map((sentence, index) => (
            <li key={`${index}-${sentence.text}`}>
              <div className="ftf-native-sentence">
                <span className="ftf-native-text" lang={targetLanguageIso}>{sentence.text}</span>{" "}
                <button
                  aria-expanded={openIndexes.has(index)}
                  className="ftf-native-explain"
                  onClick={() => toggle(index)}
                  type="button"
                >
                  {openIndexes.has(index) ? "Hide" : "Explain"}
                </button>{" "}
                <SaveToCollection cloze={sentence.cloze} quickCapture={quickCapture} sentence={sentence.text} />
              </div>
              {openIndexes.has(index) && (
                <div className="ftf-native-detail">
                  <div className="ftf-native-translation" title={`In ${baseLanguageName}`}>{sentence.translation}</div>
                  {sentence.note && <div className="ftf-native-note">{sentence.note}</div>}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

NativeVersion.propTypes = {
  baseLanguageName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  quickCapture: PropTypes.object,
  request: PropTypes.func.isRequired,
  rewriteUrl: PropTypes.string.isRequired,
  run: PropTypes.number.isRequired,
  targetLanguageIso: PropTypes.string,
  text: PropTypes.string.isRequired,
};
